/**
 * Copyright (c) 2022 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */

import { ClientConstants } from '@/client.constants';
import axios from 'axios';
import { BracketsModel, AllStateBracketsModel } from '../models/tax-models';

export default class TaxService {
    public static federalTaxBrackets: BracketsModel | null;
    public static allStateTaxBrackets: AllStateBracketsModel | null;

    private static async getFederalTaxes(): Promise<void> {
        if (!this.federalTaxBrackets) {
            const config = {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            };

            try {
                const response = await axios.get(ClientConstants.FEDERAL_TAX_URL, config);
                this.federalTaxBrackets = response.data;
            } catch(error) {
                throw new Error(ClientConstants.FEDERAL_TAX_REQUEST_ERROR + '\n' + error);
            }
        }
    }

    private static async getAllStateTaxes(): Promise<void> {
        if (!this.allStateTaxBrackets) {
            const config = {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            };

            try {
                const response = await axios.get(ClientConstants.STATE_TAX_URL, config);
                this.allStateTaxBrackets = response.data;
            } catch(error) {
                throw new Error(ClientConstants.STATE_TAX_REQUEST_ERROR + '\n' + error);
            }
        }
    }

    public static async getFederalTaxRate(filingStatus: string, annualIncome: number): Promise<any> {
        await this.getFederalTaxes();

        // search for the rate, if the search fails, set the rate to -1 to indicate that it was not found
        const rate = this.federalTaxBrackets?.filingStatusBrackets
            // search for the filing status (HH, MFS, etc)
            .find(filingStatusBracket => {
                return filingStatusBracket.status == filingStatus;
            })?.brackets
            // search for the income bracket
            .find(incomeBracket => {
                return incomeBracket.incomeLowerBound <= annualIncome &&
                    (incomeBracket.incomeUpperBound == undefined || incomeBracket.incomeUpperBound >= annualIncome);
            })?.rate ?? -1;
        const year = this.federalTaxBrackets?.year;

        if (rate == -1 || !year) {
            throw new Error(ClientConstants.FEDERAL_TAX_LOOKUP_ERROR(filingStatus, annualIncome.toFixed(0)));
        }
        return { rate, year };
    }

    public static async getMaxFederalTaxRate(filingStatus: string): Promise<any> {
        await this.getFederalTaxes();

        // search for the rate, if the search fails, set the rate to -1 to indicate that it was not found
        const rate = this.federalTaxBrackets?.filingStatusBrackets
            // search for the filing status (HH, MFS, etc)
            .find(filingStatusBracket => {
                return filingStatusBracket.status == filingStatus;
            })?.brackets
            // search for the max tax
            .map(incomeBracket => incomeBracket.rate)
            .reduce((prevRate, nextRate) => Math.max(prevRate, nextRate), -1) ?? -1;
        const year = this.federalTaxBrackets?.year;

        if (rate === -1 || !year) {
            throw Error(ClientConstants.MAX_FEDERAL_TAX_LOOKUP_ERROR(filingStatus));
        }
        return { rate, year };
    }

    public static async getStateTaxRate(state: string, filingStatus: string, annualIncome: number): Promise<any> {
        await this.getAllStateTaxes();

        const stateBrackets = this.allStateTaxBrackets?.stateBrackets
            .find(stateTaxBracket => {
                return stateTaxBracket.state == state
            });
        if (!stateBrackets) {
            throw new Error(ClientConstants.STATE_TAX_LOOKUP_ERROR(state, filingStatus, annualIncome.toFixed(0)));
        }

        // search for the rate, if the search fails, set the rate to -1 to indicate that it was not found
        const rate = stateBrackets?.filingStatusBrackets
            // search for the filing status (HH, MFS, etc)
            .find(filingStatusBracket => {
                return filingStatusBracket.status == filingStatus;
            })?.brackets
            // search for the income bracket
            .find(incomeBracket => {
                return incomeBracket.incomeLowerBound <= annualIncome &&
                    (incomeBracket.incomeUpperBound == undefined || incomeBracket.incomeUpperBound >= annualIncome);
            })?.rate ?? -1;
        const year = stateBrackets.year;

        if (rate == -1 || !year) {
            throw new Error(ClientConstants.STATE_TAX_LOOKUP_ERROR(state, filingStatus, annualIncome.toFixed(0)));
        }
        return { rate, year };
    }

    public static async getMaxStateTaxRate(state: string, filingStatus: string): Promise<any> {
        await this.getAllStateTaxes();

        const stateBrackets = this.allStateTaxBrackets?.stateBrackets
            .find(stateTaxBracket => {
                return stateTaxBracket.state == state
            });
        if (!stateBrackets) {
            throw new Error(ClientConstants.MAX_STATE_TAX_LOOKUP_ERROR(state, filingStatus));
        }

        // search for the rate, if the search fails, set the rate to -1 to indicate that it was not found
        const rate = stateBrackets?.filingStatusBrackets
            // search for the filing status (HH, MFS, etc)
            .find(filingStatusBracket => {
                return filingStatusBracket.status == filingStatus;
            })?.brackets
            // search for the income bracket
            .map(incomeBracket => incomeBracket.rate)
            .reduce((prevRate, nextRate) => Math.max(prevRate, nextRate), -1) ?? -1;
        const year = stateBrackets.year;

        if (rate == -1 || !year) {
            throw new Error(ClientConstants.MAX_STATE_TAX_LOOKUP_ERROR(state, filingStatus));
        }
        return { rate, year };
    }
}